import { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../lib/auth';
import { downloadDocument } from '../lib/documentPreview';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { DocumentPreviewModal } from '../components/DocumentPreviewModal';
import {
  FileText, Search, Trash2, Loader2, Building2, User, Calendar, Eye, Download,
  ChevronDown, ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight, AlertTriangle, CheckCircle2,
} from 'lucide-react';

/**
 * Bordro Listesi (Loonbrief List)
 * --------------------------------------------------------------
 * Şub 03, 2026 — Operatör isteğiyle eklendi:
 *   • Yüklenen tüm loonbrief PDF'leri tek bir sayfada görüntüle/listele.
 *   • Firmalara göre filtrele + gruplama, dönem (period) ve isim arama.
 *   • Görüntüle (PDF download), tekil sil, toplu sil.
 *
 * Backend: GET /api/loonbrief/list (zenginleştirilmiş kayıt) +
 *          POST /api/loonbrief/bulk-delete.
 */
const periodLabel = (p) => {
  if (!p) return '—';
  const [y, m] = p.split('-');
  const months = ['Oca','Şub','Mar','Nis','May','Haz','Tem','Ağu','Eyl','Eki','Kas','Ara'];
  const mi = parseInt(m, 10) - 1;
  return mi >= 0 && mi < 12 ? `${months[mi]} ${y}` : p;
};

const fmtSize = (n) => {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export default function LoonbriefList() {
  const [items, setItems] = useState([]);
  const [firms, setFirms] = useState([]);
  const [coverage, setCoverage] = useState(null);  // { firms:[{firm_id,firm_name,active_employees,periods}], periods:[...] }
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [firmFilter, setFirmFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('all');
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [collapsedFirms, setCollapsedFirms] = useState(new Set());
  // Önizleme modal'ı (resim/PDF/doc viewer) — sistemdeki standart
  // DocumentPreviewModal'ı kullanır; backend `/documents/{id}/download?preview=true`
  // S3'ten inline stream eder, modal zoom/sayfa kontrolleriyle gösterir.
  const [previewDoc, setPreviewDoc] = useState(null);

  // Pagination (global page size — her firma için ayrı page)
  const [pageSize, setPageSize] = useState(() => parseInt(localStorage.getItem('loonbrief_list_pagesize') || '20', 10));

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b, c] = await Promise.all([
        api.get('/loonbrief/list'),
        api.get('/firms/').catch(() => ({ data: { data: [] } })),
        api.get('/loonbrief/coverage').catch(() => ({ data: { data: null } })),
      ]);
      setItems(a.data?.data || []);
      setFirms(b.data?.data || []);
      setCoverage(c.data?.data || null);
    } catch {
      toast.error('Bordrolar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Tüm benzersiz dönemler (filtre dropdown için)
  const periods = useMemo(() => {
    const set = new Set(items.map((i) => i.period).filter(Boolean));
    return [...set].sort().reverse();
  }, [items]);

  // Filtrelenmiş kayıtlar
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return items.filter((it) => {
      if (firmFilter !== 'all' && it.firm_id !== firmFilter) return false;
      if (periodFilter !== 'all' && it.period !== periodFilter) return false;
      if (s) {
        const hay = `${it.user_full_name || ''} ${it.firm_name || ''} ${it.period || ''} ${it.file_name || ''} ${it.uploaded_by_name || ''}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [items, firmFilter, periodFilter, search]);

  // Firmaya göre grupla (tam liste)
  const grouped = useMemo(() => {
    const map = new Map();
    for (const it of filtered) {
      const key = it.firm_id || '__none__';
      if (!map.has(key)) map.set(key, { firm_id: key, firm_name: it.firm_name || '—', records: [] });
      map.get(key).records.push(it);
    }
    return [...map.values()].sort((a, b) => (a.firm_name || '').localeCompare(b.firm_name || '', 'tr'));
  }, [filtered]);

  // Firma başına ayrı sayfa numarası — { firm_id: page }
  const [firmPages, setFirmPages] = useState({});
  useEffect(() => {
    // Filtreler değişince tüm firma sayfalarını 1'e döndür
    setFirmPages({});
  }, [firmFilter, periodFilter, search, pageSize]);
  useEffect(() => {
    localStorage.setItem('loonbrief_list_pagesize', String(pageSize));
  }, [pageSize]);

  const setFirmPage = (firmId, p) => setFirmPages((prev) => ({ ...prev, [firmId]: p }));

  const toggleSelect = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  // Firma içindeki mevcut sayfayı toggle eden helper
  const toggleSelectFirmPage = (recordsOnPage) => setSelected((prev) => {
    const ids = recordsOnPage.map((r) => r.id);
    const allSelected = ids.length > 0 && ids.every((id) => prev.has(id));
    const next = new Set(prev);
    if (allSelected) for (const id of ids) next.delete(id);
    else for (const id of ids) next.add(id);
    return next;
  });
  const toggleFirmCollapse = (firmId) => setCollapsedFirms((prev) => {
    const next = new Set(prev);
    if (next.has(firmId)) next.delete(firmId); else next.add(firmId);
    return next;
  });

  const handleDelete = async (id, name) => {
    if (!window.confirm(`"${name}" için bu bordroyu silmek istediğinize emin misiniz?`)) return;
    try {
      await api.delete(`/documents/${id}`);
      toast.success('Silindi');
      setItems((prev) => prev.filter((x) => x.id !== id));
      setSelected((prev) => { const n = new Set(prev); n.delete(id); return n; });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Silinemedi');
    }
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Seçili ${selected.size} bordroyu silmek istediğinize emin misiniz?\n\nBu işlem geri alınamaz.`)) return;
    setDeleting(true);
    try {
      const res = await api.post('/loonbrief/bulk-delete', { ids: [...selected] });
      const n = res.data?.deleted || 0;
      toast.success(`${n} bordro silindi`);
      setItems((prev) => prev.filter((x) => !selected.has(x.id)));
      setSelected(new Set());
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Toplu silme başarısız');
    } finally {
      setDeleting(false);
    }
  };

  const handleDownload = async (id, fileName) => {
    // Zorla indir — Şub 27, 2026 direct CDN URL ile hızlı, fallback blob
    const ok = await downloadDocument(api, { id, original_name: fileName || 'loonbrief.pdf' });
    if (!ok) toast.error('Dosya indirilemedi');
  };

  const handlePreview = (row) => {
    // Sistemdeki birleşik önizleme modal'ı — S3'ten stream, PDF/IMG viewer ile
    // zoom+sayfa kontrolü, modal içinden "İndir" butonu, yeni sekmeye gerek yok.
    if (!row) return;
    const fileType = (row.file_name || '').toLowerCase().split('.').pop() || 'pdf';
    setPreviewDoc({
      id: row.id,
      file_name: row.file_name || 'loonbrief.pdf',
      original_name: row.file_name,
      file_type: fileType,
    });
  };

  return (
    <div className="space-y-4 max-w-7xl" data-testid="loonbrief-list-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
          <FileText className="h-6 w-6 text-blue-500" /> Bordro Listesi
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Yüklenmiş tüm loonbrief PDF&apos;lerini firmaya göre listeleyin, görüntüleyin, silin.
        </p>
      </div>

      {/* Coverage matrix — firma × dönem kapsama özeti */}
      {coverage && coverage.firms?.length > 0 && coverage.periods?.length > 0 && (
        <Card data-testid="loonbrief-coverage-matrix">
          <CardHeader className="py-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Building2 className="h-4 w-4 text-blue-500" />
              Firma × Dönem Kapsaması
              <span className="text-[10px] text-slate-400 font-normal ml-auto">
                Yüklenen / aktif çalışan
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 pb-3 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="text-left py-2 px-2 sticky left-0 bg-white dark:bg-slate-900 z-10">Firma</th>
                  <th className="text-center py-2 px-2 w-16">Aktif</th>
                  {coverage.periods.map((p) => (
                    <th key={p} className="text-center py-2 px-2 whitespace-nowrap w-24">{periodLabel(p)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {coverage.firms.map((f) => (
                  <tr key={f.firm_id} className="border-b border-slate-100 dark:border-slate-800">
                    <td className="py-1.5 px-2 font-semibold text-slate-700 dark:text-slate-200 sticky left-0 bg-white dark:bg-slate-900">{f.firm_name}</td>
                    <td className="py-1.5 px-2 text-center text-slate-500">{f.active_employees}</td>
                    {coverage.periods.map((p) => {
                      const uploaded = f.periods[p] || 0;
                      const total = f.active_employees || 0;
                      const isComplete = total > 0 && uploaded >= total;
                      const isPartial = uploaded > 0 && uploaded < total;
                      const isEmpty = uploaded === 0;
                      const color = isComplete ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                                  : isPartial   ? 'bg-amber-100 text-amber-700 border-amber-300'
                                  : isEmpty     ? 'bg-slate-50 text-slate-400 border-slate-200'
                                  : '';
                      return (
                        <td key={p} className="py-1.5 px-2 text-center">
                          <button
                            type="button"
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[11px] font-semibold ${color} hover:ring-2 hover:ring-blue-200 transition`}
                            onClick={() => { setFirmFilter(f.firm_id); setPeriodFilter(p); }}
                            title={`${f.firm_name} · ${periodLabel(p)} → filtrele`}
                            data-testid={`coverage-cell-${f.firm_id}-${p}`}
                          >
                            {isComplete && <CheckCircle2 className="h-2.5 w-2.5" />}
                            {isPartial && <AlertTriangle className="h-2.5 w-2.5" />}
                            {uploaded}<span className="opacity-60">/{total}</span>
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-2 flex items-center gap-3 text-[10px] text-slate-500">
              <span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-emerald-600" /> Tam</span>
              <span className="flex items-center gap-1"><AlertTriangle className="h-3 w-3 text-amber-600" /> Eksik</span>
              <span className="opacity-60">· Hücreye tıklayın → filtrele</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-5 pb-4 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[220px]">
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Ara</label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Çalışan adı, firma, dönem, dosya adı…"
                className="h-9 text-sm pl-7"
                data-testid="loonbrief-search"
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Firma</label>
            <select
              value={firmFilter}
              onChange={(e) => setFirmFilter(e.target.value)}
              className="block h-9 text-sm rounded border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 px-2 min-w-[150px]"
              data-testid="loonbrief-firm-filter"
            >
              <option value="all">Tüm firmalar</option>
              {firms.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Dönem</label>
            <select
              value={periodFilter}
              onChange={(e) => setPeriodFilter(e.target.value)}
              className="block h-9 text-sm rounded border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 px-2 min-w-[130px]"
              data-testid="loonbrief-period-filter"
            >
              <option value="all">Tüm dönemler</option>
              {periods.map((p) => (
                <option key={p} value={p}>{periodLabel(p)}</option>
              ))}
            </select>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Badge variant="outline" className="text-xs h-7">
              {filtered.length} / {items.length} kayıt
            </Badge>
            {selected.size > 0 && (
              <Button
                size="sm"
                variant="destructive"
                onClick={handleBulkDelete}
                disabled={deleting}
                data-testid="loonbrief-bulk-delete"
              >
                {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Trash2 className="h-3.5 w-3.5 mr-1" />}
                Seçili {selected.size} kaydı sil
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Content */}
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-400 py-8 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> Yükleniyor…
        </div>
      ) : grouped.length === 0 ? (
        <Card><CardContent className="py-16 text-center text-slate-400">
          <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p>Filtreyle eşleşen bordro yok.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-4">
          {/* Global page size selector */}
          <div className="flex items-center gap-3 px-3 text-xs text-slate-500 flex-wrap">
            <span>
              <span className="font-medium text-slate-700 dark:text-slate-200">{filtered.length}</span> kayıt · {grouped.length} firma
            </span>
            <div className="ml-auto flex items-center gap-2">
              <span>Sayfa başına:</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
                className="h-7 text-xs rounded border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 px-2"
                data-testid="loonbrief-page-size"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          {grouped.map((g) => {
            const isCollapsed = collapsedFirms.has(g.firm_id);
            // Her firma için kendi sayfa numarası (default 1)
            const firmPage = firmPages[g.firm_id] || 1;
            const firmTotalPages = Math.max(1, Math.ceil(g.records.length / pageSize));
            const fStart = (firmPage - 1) * pageSize;
            const fEnd = fStart + pageSize;
            const pagedRecords = g.records.slice(fStart, fEnd);
            const allOnPageSelected = pagedRecords.length > 0 && pagedRecords.every((r) => selected.has(r.id));
            return (
              <Card key={g.firm_id} data-testid={`loonbrief-firm-group-${g.firm_id}`}>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm flex items-center gap-2 cursor-pointer select-none" onClick={() => toggleFirmCollapse(g.firm_id)}>
                    {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    <Building2 className="h-4 w-4 text-blue-500" />
                    {g.firm_name}
                    <Badge variant="secondary" className="ml-1 text-[10px]">{g.records.length}</Badge>
                  </CardTitle>
                </CardHeader>
                {!isCollapsed && (
                  <CardContent className="pt-0 pb-3">
                    {/* Per-firm select bar */}
                    <div className="flex items-center gap-2 text-[11px] text-slate-500 pb-1.5">
                      <Checkbox
                        checked={allOnPageSelected}
                        onCheckedChange={() => toggleSelectFirmPage(pagedRecords)}
                        data-testid={`loonbrief-firm-select-all-${g.firm_id}`}
                      />
                      <span>Bu sayfadaki {pagedRecords.length} kaydı seç</span>
                      {firmTotalPages > 1 && (
                        <span className="ml-auto">
                          <span className="font-medium">{fStart + 1}-{Math.min(fEnd, g.records.length)}</span> / {g.records.length}
                        </span>
                      )}
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-200 dark:border-slate-700">
                          <tr>
                            <th className="text-left py-2 px-2 w-8"></th>
                            <th className="text-left py-2 px-2"><User className="inline h-3 w-3 mr-1" />Çalışan</th>
                            <th className="text-left py-2 px-2 w-32"><Calendar className="inline h-3 w-3 mr-1" />Dönem</th>
                            <th className="text-left py-2 px-2">Dosya</th>
                            <th className="text-right py-2 px-2 w-24">Boyut</th>
                            <th className="text-left py-2 px-2 w-44">Yükleyen</th>
                            <th className="text-left py-2 px-2 w-40">Tarih</th>
                            <th className="text-right py-2 px-2 w-24">İşlem</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pagedRecords.map((r) => (
                            <tr key={r.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/30" data-testid={`loonbrief-row-${r.id}`}>
                              <td className="py-2 px-2">
                                <Checkbox
                                  checked={selected.has(r.id)}
                                  onCheckedChange={() => toggleSelect(r.id)}
                                  data-testid={`loonbrief-check-${r.id}`}
                                />
                              </td>
                              <td className="py-2 px-2">
                                <div className="font-medium text-slate-800 dark:text-slate-100">{r.user_full_name}</div>
                                {r.user_status === 'INACTIVE' && (
                                  <span className="text-[10px] text-amber-600">Pasif kullanıcı</span>
                                )}
                              </td>
                              <td className="py-2 px-2 text-slate-600 dark:text-slate-300">{periodLabel(r.period)}</td>
                              <td className="py-2 px-2 text-xs text-slate-500 truncate max-w-xs" title={r.file_name}>{r.file_name}</td>
                              <td className="py-2 px-2 text-right text-xs text-slate-500">{fmtSize(r.file_size)}</td>
                              <td className="py-2 px-2 text-xs text-slate-500 truncate" title={r.uploaded_by_name}>{r.uploaded_by_name || '—'}</td>
                              <td className="py-2 px-2 text-xs text-slate-500">{fmtDate(r.created_at)}</td>
                              <td className="py-2 px-2 text-right">
                                <div className="inline-flex gap-1">
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-7 w-7"
                                    onClick={() => handlePreview(r)}
                                    title="Önizle"
                                    data-testid={`loonbrief-preview-${r.id}`}
                                  >
                                    <Eye className="h-3.5 w-3.5 text-blue-500" />
                                  </Button>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-7 w-7"
                                    onClick={() => handleDownload(r.id, r.file_name)}
                                    title="İndir"
                                    data-testid={`loonbrief-download-${r.id}`}
                                  >
                                    <Download className="h-3.5 w-3.5 text-emerald-600" />
                                  </Button>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-7 w-7"
                                    onClick={() => handleDelete(r.id, r.user_full_name)}
                                    title="Sil"
                                    data-testid={`loonbrief-delete-${r.id}`}
                                  >
                                    <Trash2 className="h-3.5 w-3.5 text-red-500" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Per-firm pagination */}
                    {firmTotalPages > 1 && (
                      <div className="flex items-center justify-between gap-2 pt-2 mt-1 border-t border-slate-100 dark:border-slate-800" data-testid={`loonbrief-pagination-${g.firm_id}`}>
                        <div className="text-xs text-slate-500">
                          Sayfa <span className="font-semibold text-slate-700 dark:text-slate-200">{firmPage}</span> / {firmTotalPages}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button size="icon" variant="outline" className="h-7 w-7" disabled={firmPage === 1} onClick={() => setFirmPage(g.firm_id, 1)} title="İlk sayfa" data-testid={`loonbrief-page-first-${g.firm_id}`}>
                            <ChevronsLeft className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="icon" variant="outline" className="h-7 w-7" disabled={firmPage === 1} onClick={() => setFirmPage(g.firm_id, Math.max(1, firmPage - 1))} title="Önceki" data-testid={`loonbrief-page-prev-${g.firm_id}`}>
                            <ChevronLeft className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="icon" variant="outline" className="h-7 w-7" disabled={firmPage >= firmTotalPages} onClick={() => setFirmPage(g.firm_id, Math.min(firmTotalPages, firmPage + 1))} title="Sonraki" data-testid={`loonbrief-page-next-${g.firm_id}`}>
                            <ChevronRight className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="icon" variant="outline" className="h-7 w-7" disabled={firmPage >= firmTotalPages} onClick={() => setFirmPage(g.firm_id, firmTotalPages)} title="Son sayfa" data-testid={`loonbrief-page-last-${g.firm_id}`}>
                            <ChevronsRight className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Sistemde standartlaşan birleşik önizleme modal'ı (PDF/IMG viewer +
          zoom + sayfa kontrolü + dahili "İndir" butonu). */}
      <DocumentPreviewModal
        document={previewDoc}
        open={!!previewDoc}
        onClose={() => setPreviewDoc(null)}
      />
    </div>
  );
}
